import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, MinusIcon } from 'lucide-react';
import { SectionHeading } from '../components/SectionHeading';
import { Button } from '../components/Button';
import { FRAMEWORK } from '../data/site';
const CYCLE = FRAMEWORK.filter(c => c.key !== 'operating');
const OPERATING_LAYER = ['People', 'Processes', 'Standards', 'Consistency'];
export function FrameworkOverview() {
  const [open, setOpen] = useState<string | null>('inputs');
  return <section id="framework" className="w-full scroll-mt-20 bg-cream py-20 lg:py-28" data-id="element-509">
      <div className="mx-auto max-w-7xl px-5 lg:px-8" data-id="element-510">
        <SectionHeading eyebrow="The framework" title="A simple framework for complex geology systems." body="Five steps in a continuous cycle, held together by an operating layer of people, processes, standards and consistency." data-id="element-511" />

        <div className="mt-12 space-y-3" data-id="element-512">
          {CYCLE.map((c, i) => {
          const isOpen = open === c.key;
          return <motion.div key={c.key} initial={{
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-40px'
          }} transition={{
            duration: 0.35,
            delay: i * 0.05
          }} className="overflow-hidden rounded-xl border border-charcoal/10 bg-sand" data-id="element-513">
                <button type="button" onClick={() => setOpen(isOpen ? null : c.key)} aria-expanded={isOpen} className="flex w-full items-center gap-4 px-5 py-4 text-left" data-id="element-514">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-sm font-black text-white" style={{
                backgroundColor: c.color
              }} data-id="element-515">
                    {i + 1}
                  </span>
                  <span className="flex-1" data-id="element-516">
                    <span className="block text-lg font-bold text-charcoal" data-id="element-517">
                      {c.name}
                    </span>
                    <span className="block text-sm text-warmgray" data-id="element-518">
                      {c.short}
                    </span>
                  </span>
                  {isOpen ? <MinusIcon className="h-5 w-5 text-charcoal/50" data-id="element-519" /> : <PlusIcon className="h-5 w-5 text-charcoal/50" data-id="element-520" />}
                </button>
                <AnimatePresence initial={false} data-id="element-521">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.25
              }} data-id="element-522">
                      <p className="border-t border-charcoal/10 py-4 pl-[4.5rem] pr-5 text-sm leading-relaxed text-charcoal/80" data-id="element-523">
                        {c.long}
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>

        <div className="mt-8 rounded-xl border border-charcoal/15 bg-charcoal p-6 lg:p-8" data-id="element-524">
          <p className="text-xs font-bold uppercase tracking-widest text-yellow" data-id="element-525">
            The Operating Layer
          </p>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-cream/85" data-id="element-526">
            The cycle only works when it's held together by the operating layer.
            Systems are built by people, not documents alone.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-527">
            {OPERATING_LAYER.map((label, i) => <Fragment key={label} data-id="element-528">
                <span className="rounded-md border border-cream/20 px-3.5 py-1.5 text-sm font-medium text-cream" data-id="element-529">
                  {label}
                </span>
                {i < OPERATING_LAYER.length - 1 && <span className="text-yellow" aria-hidden data-id="element-530">
                    →
                  </span>}
              </Fragment>)}
          </div>
        </div>

        <div className="mt-8" data-id="element-531">
          <Button variant="secondary" as="a" href="#resources" data-id="element-532">
            Explore the resource tools
          </Button>
        </div>
      </div>
    </section>;
}